import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Button } from '@/components/admin/ui/button';
import { Badge } from '@/components/admin/ui/badge';
import { Switch } from '@/components/admin/ui/switch';
import { Checkbox } from '@/components/admin/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/admin/ui/alert-dialog';
import { Tag, DollarSign, Percent, Gift, Trash2 } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ADD_ONS_CONFIG } from '@/data/addOnsConfig';

interface Promotion {
  id: string;
  code: string;
  name: string;
  discount_type: 'money_off' | 'percentage_off' | 'addon_free';
  discount_value: number | null;
  free_addons: string[];
  applicable_packages: string[];
  is_active: boolean;
  times_used: number;
  created_at: string;
  updated_at: string;
}

const PACKAGE_OPTIONS = [
  { value: 'photo-1', label: '1 Hour Photo' },
  { value: 'photo-2', label: '2 Hour Photo' },
  { value: 'photo-3', label: '3 Hour Photo' },
  { value: 'photo-4', label: '4 Hour Photo' },
  { value: 'photo-5', label: '5 Hour Photo' },
  { value: 'photo-6', label: '6 Hour Photo' },
  { value: 'photo-7', label: '7 Hour Photo' },
  { value: 'photo-8', label: '8 Hour Photo' },
  { value: 'photo-9', label: '9 Hour Photo' },
  { value: 'photo-10', label: '10 Hour Photo' },
  { value: 'photo-11', label: '11 Hour Photo' },
  { value: 'photo-12', label: '12 Hour Photo' },
  { value: 'video-1', label: '1 Hour Video' },
  { value: 'video-2', label: '2 Hour Video' },
  { value: 'video-3', label: '3 Hour Video' },
  { value: 'video-4', label: '4 Hour Video' },
  { value: 'video-5', label: '5 Hour Video' },
  { value: 'video-6', label: '6 Hour Video' },
  { value: 'video-7', label: '7 Hour Video' },
  { value: 'video-8', label: '8 Hour Video' },
  { value: 'video-9', label: '9 Hour Video' },
  { value: 'video-10', label: '10 Hour Video' },
  { value: 'video-11', label: '11 Hour Video' },
  { value: 'video-12', label: '12 Hour Video' },
  { value: 'photo-video-1', label: '1 Hour Photo+Video' },
  { value: 'photo-video-2', label: '2 Hour Photo+Video' },
  { value: 'photo-video-3', label: '3 Hour Photo+Video' },
  { value: 'photo-video-4', label: '4 Hour Photo+Video' },
  { value: 'photo-video-5', label: '5 Hour Photo+Video' },
  { value: 'photo-video-6', label: '6 Hour Photo+Video' },
  { value: 'photo-video-7', label: '7 Hour Photo+Video' },
  { value: 'photo-video-8', label: '8 Hour Photo+Video' },
  { value: 'photo-video-9', label: '9 Hour Photo+Video' },
  { value: 'photo-video-10', label: '10 Hour Photo+Video' },
  { value: 'photo-video-11', label: '11 Hour Photo+Video' },
  { value: 'photo-video-12', label: '12 Hour Photo+Video' },
];

const Promotions = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [dbAddonOptions, setDbAddonOptions] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Free-addon choices = hardcoded catalog + active rows admins create in the
  // addons table (so new promo add-ons like Hour on Us need no deploy)
  useEffect(() => {
    const loadDbAddons = async () => {
      const { data } = await supabaseAdmin
        .from('addons')
        .select('code, name')
        .eq('is_active', true)
        .not('code', 'is', null);
      const configIds = new Set(ADD_ONS_CONFIG.map(a => a.id));
      setDbAddonOptions(
        (data || [])
          .filter(a => a.code && !configIds.has(a.code))
          .map(a => ({ id: a.code, title: a.name }))
      );
    };
    loadDbAddons();
  }, []);
  const { toast } = useToast();

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState<Promotion | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Money Off Form State
  const [moneyOffCode, setMoneyOffCode] = useState('');
  const [moneyOffName, setMoneyOffName] = useState('');
  const [moneyOffAmount, setMoneyOffAmount] = useState('');
  const [moneyOffPackages, setMoneyOffPackages] = useState<string[]>([]);

  // Percentage Off Form State
  const [percentOffCode, setPercentOffCode] = useState('');
  const [percentOffName, setPercentOffName] = useState('');
  const [percentOffAmount, setPercentOffAmount] = useState('');
  const [percentOffPackages, setPercentOffPackages] = useState<string[]>([]);

  // Free Add-ons Form State
  const [addonCode, setAddonCode] = useState('');
  const [addonName, setAddonName] = useState('');
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [addonPackages, setAddonPackages] = useState<string[]>([]);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('promotions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPromotions(data || []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load promotions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createMoneyOffCoupon = async () => {
    if (!moneyOffCode || !moneyOffName || !moneyOffAmount || moneyOffPackages.length === 0) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields and select at least one package',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('promotions')
        .insert({
          code: moneyOffCode.toUpperCase(),
          name: moneyOffName,
          discount_type: 'money_off',
          discount_value: parseFloat(moneyOffAmount),
          applicable_packages: moneyOffPackages,
          is_active: true,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Money off coupon created',
      });

      setMoneyOffCode('');
      setMoneyOffName('');
      setMoneyOffAmount('');
      setMoneyOffPackages([]);
      fetchPromotions();
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast({
        title: 'Error',
        description: 'Failed to create coupon. Code might already exist.',
        variant: 'destructive',
      });
    }
  };

  const createPercentOffCoupon = async () => {
    if (!percentOffCode || !percentOffName || !percentOffAmount || percentOffPackages.length === 0) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields and select at least one package',
        variant: 'destructive',
      });
      return;
    }

    const percentage = parseFloat(percentOffAmount);
    if (percentage <= 0 || percentage > 100) {
      toast({
        title: 'Error',
        description: 'Percentage must be between 1 and 100',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('promotions')
        .insert({
          code: percentOffCode.toUpperCase(),
          name: percentOffName,
          discount_type: 'percentage_off',
          discount_value: percentage,
          applicable_packages: percentOffPackages,
          is_active: true,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Percentage off coupon created',
      });

      setPercentOffCode('');
      setPercentOffName('');
      setPercentOffAmount('');
      setPercentOffPackages([]);
      fetchPromotions();
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast({
        title: 'Error',
        description: 'Failed to create coupon. Code might already exist.',
        variant: 'destructive',
      });
    }
  };

  const createAddonCoupon = async () => {
    if (!addonCode || !addonName || selectedAddons.length === 0 || addonPackages.length === 0) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields, select add-ons, and select at least one package',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('promotions')
        .insert({
          code: addonCode.toUpperCase(),
          name: addonName,
          discount_type: 'addon_free',
          discount_value: null,
          free_addons: selectedAddons,
          applicable_packages: addonPackages,
          is_active: true,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Free add-ons coupon created',
      });

      setAddonCode('');
      setAddonName('');
      setSelectedAddons([]);
      setAddonPackages([]);
      fetchPromotions();
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast({
        title: 'Error',
        description: 'Failed to create coupon. Code might already exist.',
        variant: 'destructive',
      });
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabaseAdmin
        .from('promotions')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      setPromotions(promotions.map(promo =>
        promo.id === id ? { ...promo, is_active: !currentStatus } : promo
      ));

      toast({
        title: 'Success',
        description: `Coupon ${!currentStatus ? 'activated' : 'deactivated'}`,
      });
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update coupon status',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (promotion: Promotion) => {
    setPromotionToDelete(promotion);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!promotionToDelete) return;

    setDeleting(true);
    try {
      const { error } = await supabaseAdmin
        .from('promotions')
        .delete()
        .eq('id', promotionToDelete.id);

      if (error) throw error;

      setPromotions(promotions.filter(promo => promo.id !== promotionToDelete.id));

      toast({
        title: 'Success',
        description: 'Coupon deleted',
      });

      setDeleteDialogOpen(false);
      setPromotionToDelete(null);
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete coupon',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handlePackageToggle = (packageValue: string, currentPackages: string[], setter: (packages: string[]) => void) => {
    if (currentPackages.includes(packageValue)) {
      setter(currentPackages.filter(p => p !== packageValue));
    } else {
      setter([...currentPackages, packageValue]);
    }
  };

  const handleAddonToggle = (addonId: string) => {
    if (selectedAddons.includes(addonId)) {
      setSelectedAddons(selectedAddons.filter(id => id !== addonId));
    } else {
      setSelectedAddons([...selectedAddons, addonId]);
    }
  };

  const getDiscountDisplay = (promo: Promotion) => {
    if (promo.discount_type === 'money_off') {
      return `$${promo.discount_value} off`;
    } else if (promo.discount_type === 'percentage_off') {
      return `${promo.discount_value}% off`;
    } else {
      return `${promo.free_addons.length} free add-on(s)`;
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Promotions</h1>
        <p className="text-muted-foreground">Create and manage coupon codes for customers</p>
      </div>

      {/* Create Coupon Sections */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Money Off Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Money Off
            </CardTitle>
            <CardDescription>Fixed dollar amount discount</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="money-off-code">Coupon Code</Label>
              <Input
                id="money-off-code"
                placeholder="SAVE100"
                value={moneyOffCode}
                onChange={(e) => setMoneyOffCode(e.target.value)}
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="money-off-name">Display Name</Label>
              <Input
                id="money-off-name"
                placeholder="Save $100 on Any Package"
                value={moneyOffName}
                onChange={(e) => setMoneyOffName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="money-off-amount">Discount Amount ($)</Label>
              <Input
                id="money-off-amount"
                type="number"
                placeholder="100"
                value={moneyOffAmount}
                onChange={(e) => setMoneyOffAmount(e.target.value)}
              />
            </div>
            <div>
              <Label>Applicable Packages</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2 mt-2">
                {PACKAGE_OPTIONS.map(pkg => (
                  <div key={pkg.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`money-${pkg.value}`}
                      checked={moneyOffPackages.includes(pkg.value)}
                      onCheckedChange={() => handlePackageToggle(pkg.value, moneyOffPackages, setMoneyOffPackages)}
                    />
                    <label htmlFor={`money-${pkg.value}`} className="text-sm cursor-pointer">
                      {pkg.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={createMoneyOffCoupon} className="w-full">
              Create Money Off Coupon
            </Button>
          </CardContent>
        </Card>

        {/* Percentage Off Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Percentage Off
            </CardTitle>
            <CardDescription>Percentage-based discount</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="percent-off-code">Coupon Code</Label>
              <Input
                id="percent-off-code"
                placeholder="SPRING25"
                value={percentOffCode}
                onChange={(e) => setPercentOffCode(e.target.value)}
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="percent-off-name">Display Name</Label>
              <Input
                id="percent-off-name"
                placeholder="25% Off Spring Sale"
                value={percentOffName}
                onChange={(e) => setPercentOffName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="percent-off-amount">Discount Percentage (%)</Label>
              <Input
                id="percent-off-amount"
                type="number"
                placeholder="25"
                min="1"
                max="100"
                value={percentOffAmount}
                onChange={(e) => setPercentOffAmount(e.target.value)}
              />
            </div>
            <div>
              <Label>Applicable Packages</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2 mt-2">
                {PACKAGE_OPTIONS.map(pkg => (
                  <div key={pkg.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`percent-${pkg.value}`}
                      checked={percentOffPackages.includes(pkg.value)}
                      onCheckedChange={() => handlePackageToggle(pkg.value, percentOffPackages, setPercentOffPackages)}
                    />
                    <label htmlFor={`percent-${pkg.value}`} className="text-sm cursor-pointer">
                      {pkg.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={createPercentOffCoupon} className="w-full">
              Create Percentage Off Coupon
            </Button>
          </CardContent>
        </Card>

        {/* Free Add-ons Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              Free Add-ons
            </CardTitle>
            <CardDescription>Make selected add-ons free</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="addon-code">Coupon Code</Label>
              <Input
                id="addon-code"
                placeholder="FREEENGAGEMENT"
                value={addonCode}
                onChange={(e) => setAddonCode(e.target.value)}
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="addon-name">Display Name</Label>
              <Input
                id="addon-name"
                placeholder="Free Engagement Session"
                value={addonName}
                onChange={(e) => setAddonName(e.target.value)}
              />
            </div>
            <div>
              <Label>Free Add-ons</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2 mt-2">
                {[...ADD_ONS_CONFIG, ...dbAddonOptions].map(addon => (
                  <div key={addon.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`addon-${addon.id}`}
                      checked={selectedAddons.includes(addon.id)}
                      onCheckedChange={() => handleAddonToggle(addon.id)}
                    />
                    <label htmlFor={`addon-${addon.id}`} className="text-sm cursor-pointer">
                      {addon.title}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Applicable Packages</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2 mt-2">
                {PACKAGE_OPTIONS.map(pkg => (
                  <div key={pkg.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`addon-pkg-${pkg.value}`}
                      checked={addonPackages.includes(pkg.value)}
                      onCheckedChange={() => handlePackageToggle(pkg.value, addonPackages, setAddonPackages)}
                    />
                    <label htmlFor={`addon-pkg-${pkg.value}`} className="text-sm cursor-pointer">
                      {pkg.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={createAddonCoupon} className="w-full">
              Create Free Add-on Coupon
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Active Coupons List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            All Coupons
          </CardTitle>
          <CardDescription>Manage existing coupon codes</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : promotions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No coupons created yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Packages</TableHead>
                  <TableHead>Times Used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promotions.map((promo) => (
                  <TableRow key={promo.id}>
                    <TableCell className="font-mono font-semibold">{promo.code}</TableCell>
                    <TableCell>{promo.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {promo.discount_type === 'money_off' && 'Money Off'}
                        {promo.discount_type === 'percentage_off' && '% Off'}
                        {promo.discount_type === 'addon_free' && 'Free Add-ons'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getDiscountDisplay(promo)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {promo.applicable_packages.length} package(s)
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{promo.times_used}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={promo.is_active}
                          onCheckedChange={() => toggleActive(promo.id, promo.is_active)}
                        />
                        <span className="text-sm">
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(promo)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the coupon <strong>{promotionToDelete?.code}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Promotions;
